import type { EntityKind, RegistryKind } from '../../types/api'

/**
 * What differs between a school, a college and a company registration.
 *
 * <p>Data rather than three near-identical components: the entity specs describe the same four
 * steps three times, changing only the labels, the option lists and which registry is quoted.
 * Three copies of the wizard would drift the moment one of them gained a field.
 */
export interface InstitutionConfig {
  entityKind: Extract<EntityKind, 'SCHOOL' | 'COLLEGE' | 'COMPANY'>
  /** "school", used in prose: "Your school's name...". */
  noun: string
  title: string
  /** Step 1 heading, e.g. "School details". */
  detailsTitle: string
  typeLabel: string
  typeOptions: string[]
  /**
   * Whether this kind is asked for a registry code at all.
   *
   * <p>Currently false for all three, on request. The field, the DB column and the server-side
   * check all remain, so switching one back on is this flag alone.
   *
   * <p>Worth knowing what is given up while they are off: the code was the only thing enforcing
   * one institution per UDISE+ / AISHE / CIN - a second registration on the same code was refused
   * and flagged as possible impersonation - and it was what the visiting team matched against the
   * public register. Two registrations claiming the same institution are now indistinguishable to
   * the platform, and a reviewer has the name, the address and (for a company) the website.
   */
  showRegistry: boolean
  registryKind: RegistryKind
  registryLabel: string
  /** Shown under the field and reused as the error when the format is wrong. */
  registryHelp: string
  registryPattern: RegExp
  showYearEstablished: boolean
  showDescription: boolean
  /** Extra multi-select questions kept in `attributes`. */
  attributeFields: { key: string; label: string; options: string[]; required?: boolean }[]
  coordinatorTitle: string
  designationOptions: string[]
  /**
   * Whether a website is asked for at all.
   *
   * <p>Only a company. A school or a college is verified by a visit to the premises, so its site
   * was never part of that check - it was one more optional field on an already long form. A
   * company's site is the check, which is why it stays there and stays required.
   */
  showWebsite: boolean
  /** Whether a website is required - a company is checked against its site. */
  websiteRequired: boolean
  /** The public-facing consent line on step 4. */
  consentLine: string
}

export const INSTITUTION_CONFIG: Record<string, InstitutionConfig> = {
  school: {
    entityKind: 'SCHOOL',
    noun: 'school',
    title: 'Register your school',
    detailsTitle: 'School details',
    typeLabel: 'School type',
    typeOptions: ['Government', 'Government-aided', 'Private unaided', 'International', 'Other'],
    showRegistry: false,
    registryKind: 'UDISE',
    registryLabel: 'UDISE+ code',
    registryHelp: 'Enter the 11-digit UDISE+ code',
    registryPattern: /^\d{11}$/,
    showYearEstablished: false,
    showDescription: false,
    attributeFields: [
      {
        key: 'boards',
        label: 'Board affiliation',
        options: ['CBSE', 'ICSE', 'State Board', 'IB', 'Cambridge', 'Other'],
        required: true,
      },
    ],
    coordinatorTitle: 'Coordinator',
    designationOptions: [
      'Principal',
      'Vice-principal',
      'Admissions coordinator',
      'Administrator',
      'Other',
    ],
    showWebsite: false,
    websiteRequired: false,
    consentLine:
      "Your school's name, area, classes and boards are shown to families nearby. Your coordinator's mobile and email are shared only after you accept a connection.",
  },

  college: {
    entityKind: 'COLLEGE',
    noun: 'college',
    title: 'Register your college',
    detailsTitle: 'College details',
    typeLabel: 'Institution type',
    typeOptions: [
      'University',
      'Constituent college',
      'Affiliated college',
      'Autonomous college',
      'Standalone institute',
      'Other',
    ],
    showRegistry: false,
    registryKind: 'AISHE',
    registryLabel: 'AISHE code',
    registryHelp: 'Enter the AISHE code, for example C-12345',
    registryPattern: /^[CUIcui]-\d{3,6}$/,
    showYearEstablished: false,
    showDescription: false,
    attributeFields: [
      {
        key: 'approvals',
        label: 'Approvals and accreditation',
        options: ['UGC', 'AICTE', 'NMC', 'PCI', 'BCI', 'NCTE', 'NAAC', 'NBA', 'Other'],
        required: true,
      },
    ],
    coordinatorTitle: 'Admissions coordinator',
    designationOptions: [
      'Principal',
      'Dean',
      'Registrar',
      'Admissions head',
      'Admissions coordinator',
      'Other',
    ],
    showWebsite: false,
    websiteRequired: false,
    consentLine:
      "Your college's name, area, fields of study and levels are shown to learners nearby. Your coordinator's mobile and email are shared only after you accept a connection.",
  },

  corporate: {
    entityKind: 'COMPANY',
    noun: 'company',
    title: 'Register your company',
    detailsTitle: 'Company details',
    typeLabel: 'Organisation type',
    typeOptions: [
      'Private limited',
      'Public limited',
      'LLP',
      'Partnership / Proprietorship',
      'MSME / Startup',
      'Section 8 / NGO',
      'Government / PSU',
      'Other',
    ],
    // Kept for when a registry is asked for again: a company may quote any of three, CIN by
    // default, and the identifier-type chips choose between them.
    showRegistry: false,
    registryKind: 'CIN',
    registryLabel: 'Registration identifier',
    registryHelp: 'CIN (21 characters), GSTIN (15) or Udyam (UDYAM-XX-00-0000000)',
    registryPattern: /^([A-Za-z0-9]{21}|[A-Za-z0-9]{15}|UDYAM-[A-Za-z]{2}-\d{2}-\d{7})$/,
    showYearEstablished: false,
    showDescription: false,
    attributeFields: [
      {
        key: 'industry',
        label: 'Industry',
        options: [
          'IT & Software',
          'Education',
          'Healthcare',
          'Finance & Banking',
          'Manufacturing',
          'Retail & E-commerce',
          'Media & Advertising',
          'Hospitality',
          'Consulting',
          'Non-profit',
          'Government',
          'Other',
        ],
        required: true,
      },
      {
        key: 'company_size',
        label: 'Company size',
        options: ['1-10', '11-50', '51-200', '201-1000', '1000+'],
        required: true,
      },
    ],
    coordinatorTitle: 'Internship coordinator',
    designationOptions: [
      'HR manager',
      'Hiring manager',
      'Founder / Director',
      'Campus relations',
      'Other',
    ],
    // Required: it is what the visiting team checks the company against.
    showWebsite: true,
    websiteRequired: true,
    consentLine:
      "Your company's name, area and internship areas are shown to learners nearby. Your coordinator's mobile and email are shared only after you accept a connection.",
  },
}
